"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.defaultProps = exports.default = exports.HeroHalfImage = void 0;
require("./HeroHalfImage.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _actionFormatter = require("../../util/actionFormatter/actionFormatter");
var _getButtonHtml = require("../../util/getButtonHtml/getButtonHtml");
var _getLinkHtml = require("../../util/getLinkHtml/getLinkHtml");
var _renderObject = require("../../util/renderObject/renderObject");
var _GridContainer = _interopRequireDefault(require("../GridContainer/GridContainer"));
var _HeroBody = _interopRequireDefault(require("../HeroBody/HeroBody"));
var _HeroFooter = _interopRequireDefault(require("../HeroFooter/HeroFooter"));
var _HeroHeader = _interopRequireDefault(require("../HeroHeader/HeroHeader"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Represents a HeroHalfImage component
 *
 * @method
 * @param {HeroHalfImageProps} props
 * @returns HeroHalfImage
 */

const HeroHalfImage = props => {
  const {
    actionGroups,
    backgroundColor = defaultProps.backgroundColor,
    body,
    externalAction,
    externalActionFullyClick,
    externalMedia,
    eyebrow,
    footerStyle,
    fullyClickableParent,
    headline,
    headlineTag = defaultProps.headlineTag,
    image,
    tag,
    textColor = defaultProps.textColor,
    theme = defaultProps.theme
  } = props;
  let formattedActionFullyClick;
  const hasText = body || eyebrow || headline || actionGroups || tag;
  const hasImage = image || externalMedia;
  const isKnockoutLink = textColor === 'content-knockout';
  const paletteOneAction = actionGroups && actionGroups.actions?.length === 1;
  const hasOneAction = fullyClickableParent || paletteOneAction;
  if (paletteOneAction) {
    formattedActionFullyClick = (0, _actionFormatter.actionFormatter)({
      action: actionGroups?.actions[0],
      types: actionGroups?.types,
      hasOneAction: hasOneAction,
      derivedAriaLabel: actionGroups?.actions[0].label,
      fullyClickableParent: paletteOneAction
    }, {
      getButtonMarkup: _getButtonHtml.getButtonHtml,
      getLinkMarkup: _getLinkHtml.getLinkHtml
    });
  }
  const componentClassName = (0, _classnames.default)('pal-c-HeroHalfImage', {
    'pal-c-HeroHalfImage--backgroundColor': backgroundColor,
    [`pal-c-HeroHalfImage--oneAction`]: hasOneAction
  });
  const containerStyle = {
    '--backgroundColor': `var( --pal-colors-${theme}-${backgroundColor} )`
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: containerStyle
  }, hasOneAction && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, formattedActionFullyClick && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(formattedActionFullyClick)), externalActionFullyClick && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(externalActionFullyClick))), /*#__PURE__*/_react.default.createElement(_GridContainer.default, null, hasImage && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-HeroHalfImage__image"
  }, /*#__PURE__*/_react.default.createElement(_HeroHeader.default, {
    externalMedia: externalMedia,
    image: {
      desktopImage: image?.image,
      tabletImage: image?.tabletImage,
      mobileImage: image?.mobileImage,
      altText: image?.altText,
      loading: image?.loading
    }
  })), hasText && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-HeroHalfImage__bodyAndAction"
  }, /*#__PURE__*/_react.default.createElement(_HeroBody.default, {
    backgroundColor: backgroundColor,
    body: body,
    bodyStyle: "body-xlg",
    eyebrow: eyebrow,
    headline: headline,
    headlineStyle: "display",
    headlineTag: headlineTag,
    tag: tag,
    textColor: textColor
  }), /*#__PURE__*/_react.default.createElement(_HeroFooter.default, _extends({}, actionGroups, {
    externalAction: externalAction,
    isKnockoutLink: isKnockoutLink,
    style: footerStyle
  })))));
};

/**
 * Property type definitions
 * @typedef HeroHalfImageProps
 * @type {object}
 * @property {object} actionGroups - Allows passing an object to the actionGroup prop
 * @property {string} backgroundColor - sets background color for the hero
 * @property {string} body - body text/description of the hero body
 * @property {object} externalAction - external action object
 * @property {object} externalImage - brings in the UltaAsset
 * @property {string} eyebrow - smaller text above the headline
 * @property {string} footerStyle - style for footer
 * @property {string} headline - headline text of the hero body
 * @property {string} headlineTag - headline tag of the hero body
 * @property {object} image - desktopImage, tabletImage, mobileImage, type, altText, ratio and loading prop for Image component
 * @property {object} primaryAction - Allows passing an object to the primaryAction prop
 * @property {object} secondaryAction - Allows passing an object to the secondaryAction prop
 * @property {object} tag - tag component above the eyebrow
 * @property {string} textColor - sets the color of the text to default or knockout
 */
exports.HeroHalfImage = HeroHalfImage;
const propTypes = {
  /**
   * Allows passing an object to the actionGroups prop
   */
  actionGroups: _propTypes.default.object,
  /**
   * Backgroundcolor for hero
   */
  backgroundColor: _propTypes.default.string,
  /**
   * body text/description of the hero body
   */
  body: _propTypes.default.string,
  /**
   * Sets the primary action from an external source that contains business logic
   */
  externalAction: _propTypes.default.object,
  /**
   * Brings in the UltaAsset
   */
  externalImage: _propTypes.default.object,
  /**
   * Smaller text above the headline
   */
  eyebrow: _propTypes.default.string,
  /**
   * style for footer
   */
  footerStyle: _propTypes.default.string,
  /**
   * headline text of the hero body
   */
  headline: _propTypes.default.string,
  /**
   * HTML tag for the headline
   */
  headlineTag: _propTypes.default.string,
  /**
   * desktopImage, tabletImage, mobileImage, type, altText, ratio and loading prop for Image component
   */
  image: _propTypes.default.object,
  /**
   * Allows passing an object to the primaryAction prop
   */
  primaryAction: _propTypes.default.object,
  /**
   * Allows passing an object to the secondaryAction prop
   */
  secondaryAction: _propTypes.default.object,
  /**
   * tag component above the eyebrow
   */
  tag: _propTypes.default.object,
  /**
   * Sets the color of the text to default or knockout
   */
  textColor: _propTypes.default.oneOf(['content-default', 'content-knockout']),
  /**
   * Sets the theme
   */
  theme: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} headlineTag - headlineTag: 'h2' - sets the default for the headline tag
 * @property {string} textColor - textColor: 'content-default' - sets the color of the text
 * @property {string} theme -  theme: 'default' - sets the theme at the component level
 * @property {string} backgroundColor - sets the background color
 */
const defaultProps = exports.defaultProps = {
  headlineTag: 'h2',
  textColor: 'content-default',
  theme: 'default',
  backgroundColor: 'background-default'
};
HeroHalfImage.propTypes = propTypes;
var _default = exports.default = HeroHalfImage;